/**
 * Crop helper — takes an image source + pixel crop area,
 * returns a compressed JPEG blob.
 */
export interface PixelCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.setAttribute("crossOrigin", "anonymous");
    img.src = url;
  });

export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: PixelCrop,
  outputSize = 500,
  quality = 0.8
): Promise<File> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d")!;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputSize,
    outputSize
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" }));
        } else {
          reject(new Error("Canvas is empty"));
        }
      },
      "image/jpeg",
      quality
    );
  });
}

/**
 * Auto-enhance an image using Canvas CSS filters.
 * Applies brightness(1.2) contrast(1.1) saturate(1.1).
 * Outputs JPEG at quality 0.9, compresses to ≤1 MB if needed.
 */
export async function enhanceImage(file: File): Promise<File> {
  const url = URL.createObjectURL(file);
  try {
    const image = await createImage(url);
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const ctx = canvas.getContext("2d")!;

    // Apply enhancement filters
    ctx.filter = "brightness(1.2) contrast(1.1) saturate(1.1)";
    ctx.drawImage(image, 0, 0);

    // Convert to JPEG, try quality 0.9 first
    let quality = 0.9;
    let blob = await canvasToBlob(canvas, quality);

    // Compress further if >1 MB
    while (blob.size > 1024 * 1024 && quality > 0.5) {
      quality -= 0.1;
      blob = await canvasToBlob(canvas, quality);
    }

    return new File([blob], `enhanced_${Date.now()}.jpg`, { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas is empty"));
      },
      "image/jpeg",
      quality
    );
  });
}
